/**
 * Pricing Layer Helpers
 *
 * Utilities for creating Mapbox GL JS expressions for pricing H3 hexagon vector tiles
 */

import type { DynamicLayerConfig } from '../config/dynamicLayersConfig'

/**
 * Mapping from layer ID to Elasticsearch index name
 */
const INDEX_MAP: Record<string, string> = {
  'purchase-market-house-price': 'estimate-purchase-house-v2.1',
  'purchase-market-apartment-price': 'estimate-purchase-apartment-v2.1',
  'rental-market-house-price': 'estimate-rent-house-v2.1',
  'rental-market-apartment-price': 'estimate-rent-apartment-v2.1',
}

/**
 * Value ranges for pricing layers (CHF per m²)
 */
const MIN_PURCHASE_PRICE = 4000
const MAX_PURCHASE_PRICE = 19000
const DIFF_PURCHASE_PRICE = MAX_PURCHASE_PRICE - MIN_PURCHASE_PRICE
export const PURCHASE_PRICE_RANGES = [
  MIN_PURCHASE_PRICE,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 1,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 2,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 3,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 4,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 5,
  MIN_PURCHASE_PRICE + Math.round(DIFF_PURCHASE_PRICE / 7) * 6,
  MAX_PURCHASE_PRICE,
]
const MIN_YEARLY_RENTAL_PRICE = 180
const MAX_YEARLY_RENTAL_PRICE = 550
const DIFF_YEARLY_RENTAL_PRICE =
  MAX_YEARLY_RENTAL_PRICE - MIN_YEARLY_RENTAL_PRICE
export const RENTAL_PRICE_RANGES = [
  MIN_YEARLY_RENTAL_PRICE,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 1,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 2,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 3,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 4,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 5,
  MIN_YEARLY_RENTAL_PRICE + Math.round(DIFF_YEARLY_RENTAL_PRICE / 7) * 6,
  MAX_YEARLY_RENTAL_PRICE,
]
const VALUE_RANGES: Record<string, number[]> = {
  // Purchase prices (CHF/m²)
  'purchase-market-house-price': PURCHASE_PRICE_RANGES,
  'purchase-market-apartment-price': PURCHASE_PRICE_RANGES,

  // Rental prices (CHF/m²/month)
  'rental-market-house-price': RENTAL_PRICE_RANGES,
  'rental-market-apartment-price': RENTAL_PRICE_RANGES,
}
/**
 * Color schemes for pricing layers
 * These match the colors in dynamicLayersConfig.ts
 */
export const PRICING_COLORS = [
  '#3288bd',
  '#66c2a5',
  '#abdda4',
  '#e6f598',
  '#fee08b',
  '#fdae61',
  '#f46d43',
  '#d53e4f',
]

/**
 * Check if a layer is a pricing layer
 */
export function isPricingLayer(layerId: string): boolean {
  return (
    layerId.startsWith('purchase-market-') ||
    layerId.startsWith('rental-market-')
  )
}

/**
 * Get the Elasticsearch index name for a pricing layer
 */
export function getPricingIndex(layerId: string): string | undefined {
  return INDEX_MAP[layerId]
}

/**
 * Get the aggregation field name for pricing layers
 * Elasticsearch searchMvt returns aggregations with _aggregation prefix
 * and the value is nested under .value property
 */
export function getPricingField(layerId: string): string {
  return 'avg_price_m2.value'
}

/**
 * Create Mapbox fill-color expression for a pricing layer
 *
 * Returns a Mapbox expression that maps price values to colors
 */
export function createPricingFillColorExpression(
  layerId: string,
  layerConfig: DynamicLayerConfig
): any {
  const colors = layerConfig.render?.colors || []
  const field = getPricingField(layerId)
  const values = VALUE_RANGES[layerId]

  if (!values) {
    console.warn(`No value range defined for pricing layer: ${layerId}`)
    return colors[0] || '#CCCCCC'
  }

  // Build interpolation stops: value1, color1, value2, color2, ...
  const stops: any[] = []
  for (let i = 0; i < Math.min(values.length, colors.length); i++) {
    stops.push(values[i], colors[i])
  }

  return ['interpolate', ['linear'], ['get', field], ...stops]
}

/**
 * Format a pricing value for display in tooltips
 */
export function formatPricingValue(
  layerId: string,
  value: number | string | undefined
): string {
  if (value === undefined || value === null) {
    return 'No data'
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(numValue)) {
    return 'No data'
  }

  // Format as currency with thousands separator
  const formatted = new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue)

  // Add /m² suffix
  return `${formatted}/m²`
}

/**
 * Get the display name for a pricing layer (for tooltip title)
 */
export function getPricingLayerDisplayName(layerId: string): string {
  const names: Record<string, string> = {
    'purchase-market-house-price': 'Avg Purchase Price - House',
    'purchase-market-apartment-price': 'Avg Purchase Price - Apartment',
    'rental-market-house-price': 'Avg Rental Price - House',
    'rental-market-apartment-price': 'Avg Rental Price - Apartment',
  }
  return names[layerId] || layerId
}

/**
 * Get all pricing layer IDs
 */
export function getAllPricingLayerIds(): string[] {
  return Object.keys(INDEX_MAP)
}

/**
 * Check if a layer is a purchase market layer
 */
export function isPurchaseMarketLayer(layerId: string): boolean {
  return layerId.startsWith('purchase-market-')
}

/**
 * Check if a layer is a rental market layer
 */
export function isRentalMarketLayer(layerId: string): boolean {
  return layerId.startsWith('rental-market-')
}

/**
 * Pricing layer Z-ordering (bottom to top)
 * Later items in array render on top
 */
export const PRICING_LAYER_ORDER = [
  'purchase-market-apartment-price', // Bottom
  'purchase-market-house-price',
  'rental-market-apartment-price',
  'rental-market-house-price', // Top (default visible for rental)
]

/**
 * Get API URL for pricing vector tiles
 */
export function getPricingVectorTileUrl(
  apiBaseUrl: string,
  index: string
): string {
  return `${apiBaseUrl}/pricing/tiles/{z}/{x}/{y}?index=${index}`
}

/**
 * Pricing vector tile source configuration
 */
export function createPricingVectorTileSource(
  apiBaseUrl: string,
  index: string
) {
  return {
    type: 'vector' as const,
    tiles: [getPricingVectorTileUrl(apiBaseUrl, index)],
    minzoom: 10,
    maxzoom: 16,
  }
}
